import React, { useState } from "react";
import { X, Receipt, Check } from "@phosphor-icons/react";
import { api } from "../lib/api";
import { toast } from "sonner";

/**
 * Modal "Demander un devis" — affiché sur les fiches Premium IA.
 *
 * Le visiteur remplit (nom, email, téléphone, message, budget facultatif).
 * Le formulaire crée une `lead` côté backend → email envoyé au commerçant +
 * notif admin. Pas de spam : 1 demande / IP / business / 24 h (rate-limité côté backend).
 */
export default function QuoteRequestModal({ business, isOpen, onClose }) {
  const [form, setForm] = useState({
    name: "", email: "", phone: "", message: "", budget: "",
  });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Nom, email et message sont obligatoires");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/public/quote-request/${business.slug}`, form);
      setSent(true);
      toast.success("Demande envoyée — le commerçant vous contactera sous 24-48 h");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Envoi impossible");
    } finally {
      setBusy(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[70] bg-black/60 backdrop-blur-sm grid place-items-center px-4 py-8 overflow-y-auto"
      onClick={onClose}
      data-testid="quote-modal-backdrop"
    >
      <div
        className="w-full max-w-md bg-card border border-border rounded-sm shadow-xl"
        onClick={(e) => e.stopPropagation()}
        data-testid="quote-modal"
      >
        <div className="flex items-start justify-between gap-3 p-5 border-b border-border">
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 bg-secondary/10 text-secondary grid place-items-center rounded-sm shrink-0">
              <Receipt size={18} weight="duotone"/>
            </div>
            <div>
              <h2 className="font-display text-lg font-medium leading-tight">Demander un devis</h2>
              <p className="text-xs text-muted-foreground mt-0.5">à {business.name}</p>
            </div>
          </div>
          <button onClick={onClose} aria-label="Fermer" className="text-muted-foreground hover:text-foreground" data-testid="quote-modal-close">
            <X size={20} weight="bold"/>
          </button>
        </div>

        {sent ? (
          <div className="p-8 text-center" data-testid="quote-sent">
            <div className="w-14 h-14 bg-primary/10 text-primary grid place-items-center rounded-full mx-auto mb-4">
              <Check size={28} weight="bold"/>
            </div>
            <h3 className="font-display text-xl font-medium mb-2">Demande envoyée ✓</h3>
            <p className="text-sm text-muted-foreground mb-6">
              <b>{business.name}</b> a reçu votre demande de devis. Réponse sous 24-48 h.
            </p>
            <button
              onClick={onClose}
              className="bg-primary text-primary-foreground hover:bg-[#0f3d29] hover:text-white px-5 py-2.5 rounded-sm text-sm font-semibold"
              data-testid="quote-close-after-send"
            >
              Fermer
            </button>
          </div>
        ) : (
          <form onSubmit={submit} className="p-5 grid gap-3">
            <div>
              <label className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground mb-1 block">Votre nom *</label>
              <input
                required
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="w-full border border-border rounded-sm px-3 py-2 text-sm bg-background outline-none focus:border-primary"
                data-testid="quote-name"
              />
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <label className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground mb-1 block">Email *</label>
                <input
                  required
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="w-full border border-border rounded-sm px-3 py-2 text-sm bg-background outline-none focus:border-primary"
                  data-testid="quote-email"
                />
              </div>
              <div>
                <label className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground mb-1 block">Téléphone</label>
                <input
                  type="tel"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  className="w-full border border-border rounded-sm px-3 py-2 text-sm bg-background outline-none focus:border-primary"
                  data-testid="quote-phone"
                />
              </div>
            </div>
            <div>
              <label className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground mb-1 block">Budget approximatif <span className="opacity-60">(facultatif)</span></label>
              <select
                value={form.budget}
                onChange={(e) => setForm({ ...form, budget: e.target.value })}
                className="w-full border border-border rounded-sm px-3 py-2 text-sm bg-background outline-none focus:border-primary"
                data-testid="quote-budget"
              >
                <option value="">— Choisir —</option>
                <option value="lt_500">Moins de 500 €</option>
                <option value="500_2000">500 € — 2 000 €</option>
                <option value="2000_5000">2 000 € — 5 000 €</option>
                <option value="5000_15000">5 000 € — 15 000 €</option>
                <option value="gt_15000">Plus de 15 000 €</option>
                <option value="unknown">Je ne sais pas</option>
              </select>
            </div>
            <div>
              <label className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground mb-1 block">Décrivez votre besoin *</label>
              <textarea
                required
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                rows={4}
                placeholder="Décrivez ce que vous recherchez : type de prestation, quantité, délai souhaité…"
                className="w-full border border-border rounded-sm px-3 py-2 text-sm bg-background outline-none focus:border-primary"
                data-testid="quote-message"
              />
            </div>
            <button
              type="submit"
              disabled={busy}
              className="bg-secondary text-secondary-foreground hover:bg-[#b56847] hover:text-white px-4 py-3 rounded-sm text-sm font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-50"
              data-testid="quote-submit"
            >
              <Receipt size={16} weight="bold"/> {busy ? "Envoi…" : "Envoyer ma demande de devis"}
            </button>
            <p className="text-[11px] text-muted-foreground text-center">
              Aucun compte requis · Le commerçant vous répond directement par email
            </p>
          </form>
        )}
      </div>
    </div>
  );
}
